import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Loader2, FileText } from 'lucide-react';
import { api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const LegalAcceptancePage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Slugs da accettare: passati via query param ?slugs=a,b,c o state di navigazione
  const getSlugsFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('slugs');
    return raw ? raw.split(',').filter(Boolean) : [];
  };

  const slugsToAccept = location.state?.slugs_to_accept || getSlugsFromUrl();
  const returnTo = location.state?.returnTo || '/';

  const [docs, setDocs] = useState([]);
  const [checked, setChecked] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchDocs = async () => {
      setLoading(true);
      const results = [];
      for (const slug of slugsToAccept) {
        try {
          const res = await api.get(`/legal/documents/${slug}`);
          results.push(res.data);
        } catch {
          // ignora documenti non trovati
        }
      }
      setDocs(results);
      const initial = {};
      results.forEach(d => { initial[d.slug] = false; });
      setChecked(initial);
      setLoading(false);
    };

    if (slugsToAccept.length > 0) {
      fetchDocs();
    } else {
      setLoading(false);
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const allChecked = docs.length > 0 && docs.every(d => checked[d.slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allChecked) {
      toast.error('Devi accettare tutti i documenti per continuare');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/legal/accept', { slugs: docs.map(d => d.slug) });
      toast.success('Documenti accettati. Grazie!');
      navigate(returnTo, { replace: true });
    } catch (error) {
      const msg = error.response?.data?.detail || 'Errore durante il salvataggio';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (slugsToAccept.length === 0 || docs.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4">
        <h1 className="text-2xl font-bold text-text-primary">Nessun documento da accettare</h1>
        <Link to="/" className="text-primary underline">Torna alla home</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center py-12 px-4">
      <div className="max-w-lg w-full">
        <div className="bg-white rounded-xl border border-border p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold text-text-primary">
                Aggiornamento documenti legali
              </h1>
              <p className="text-sm text-text-secondary">
                Per continuare, accetta i documenti aggiornati.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {docs.map(doc => (
              <label
                key={doc.slug}
                className="flex items-start gap-3 p-4 rounded-lg border border-border cursor-pointer hover:bg-background transition-colors"
              >
                <input
                  type="checkbox"
                  className="mt-1 flex-shrink-0"
                  checked={checked[doc.slug] || false}
                  onChange={e => setChecked(prev => ({ ...prev, [doc.slug]: e.target.checked }))}
                />
                <span className="text-sm text-text-secondary">
                  Ho letto e accetto{' '}
                  <Link
                    to={`/legal/${doc.slug}`}
                    target="_blank"
                    className="text-primary underline font-medium"
                    onClick={e => e.stopPropagation()}
                  >
                    {doc.title}
                  </Link>
                  {' '}(v{doc.version})
                </span>
              </label>
            ))}

            <Button
              type="submit"
              className="w-full mt-2"
              size="lg"
              disabled={!allChecked || submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                'Accetta e continua'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LegalAcceptancePage;
